import { KeepAlive, reactive, onMount, onCleanup } from 'cdui-js';
import {
  initLineTheme,
  initTheme,
  applyTheme,
  applySafeArea,
  loadSysConfigurations,
  applySysConfigurations,
} from '../stores/common';
import { initBusConfigs, applyCountryFromApp, applyBusConfigsFromApp, initCountry, initBusConfigsFromApi } from '../stores/bus-config';
import {
  getUserInfo,
  applyUserInfo,
  initUserClientType,
  isAppUser,
  userState,
} from '../stores/user';
import { applyApplySeedFromTheme } from '../stores/apply-seed';
import { applyRNUserToken, initRNBridge, waitForRNToken } from '../lib/rnBridge';
import { isOpenAccountDocument, waitForOpenAccountBootstrap } from './mobile/account/open-progress/openAccountSession';
import { redirectH5ToOpenProgressIfNeeded } from './mobile/utils/openAccount';
import { initAnalytics } from '../analytics';
import { applyH5Lang } from '../i18n';
import './common.css';

// 暂统一走移动端；PC 用 430×932 壳承载。未来 PC 版再挂 PCHost
import { MobileHost } from './mobile';


/**
 * 浏览器 ?sysEnv=app 只模拟版式。读到一次就记住，避免 routeTo 丢掉 query 后退出铺开模式。
 * 不当作 ReactNativeWebView，页面仍走 H5，不调原生桥。
 */
let appShellPreviewLatched = false;

/** APP WebView，或浏览器 ?sysEnv=app：按屏幕铺开，宽最多 1400 */
const isAppShellPreview = () => {
  if (typeof window === 'undefined') return false;
  if ((window as any).ReactNativeWebView) return true;
  if (appShellPreviewLatched) return true;
  try {
    if (new URLSearchParams(window.location.search).get('sysEnv') === 'app') {
      appShellPreviewLatched = true;
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
};

/** 电脑浏览器宽屏才用 430×932 手机壳；APP / ?sysEnv=app / 窄屏不加壳 */
const isPcShellViewport = () =>
  typeof window !== 'undefined' && window.innerWidth > 480 && !isAppShellPreview();

/**
 * Theme 字典字段：App 可能下发对象，也可能二次 JSON.stringify 成字符串。
 * 统一成非空 plain object；无效 / 空 → null。
 */
const normalizeThemeDict = (v: unknown): Record<string, unknown> | null => {
  if (v == null || v === '') return null;
  let data: unknown = v;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  if (Object.keys(data as object).length === 0) return null;
  return data as Record<string, unknown>;
};

/** 等本次 App token 后再兜底请求（刷新场景 Theme 可能先到且为空） */
const afterRNToken = (fn: () => void | Promise<void>) => {
  void waitForRNToken()
    .then(() => fn())
    .catch(() => {});
};

// 设备id、userid
export const createId = () => {
  if (localStorage.getItem('DIFI_DEVICE_ID')) {
    return localStorage.getItem('DIFI_DEVICE_ID');
  }
  // 获取时间戳当唯一标识
  const userId =
    userState.deviceInfo?.deviceId ||
    `${Date.now()}${Math.floor(Math.random() * 10000)}${Math.random().toString(36).slice(2, 6)}`;

  localStorage.setItem('DIFI_DEVICE_ID', userId as any);

  return userId;
};

/**
 * App WebView：等 token 后只处理登录态；configurations / country / busConfigs / userInfo 由 Theme 灌入，不请求。
 * 纯 H5：token 就绪后拉配置/用户信息；字典并行。
 */
const bootstrapAppData = async () => {
  const openAccountDocument = isOpenAccountDocument();
  try {
    if (openAccountDocument) {
      await waitForOpenAccountBootstrap();
    } else {
      await waitForRNToken();
    }
  } catch {
    // WebView 未拿到 token：不发起鉴权请求
    return;
  }

  if (openAccountDocument && isAppUser()) {
    return;
  }

  const hasToken = !!localStorage.getItem('token');
  if (hasToken) {
    userState.isLogin = true;
  }

  // App：configurations / country / busConfigs / userInfo / apply seed 由 APP_TO_WEB_Theme 下发
  if (isAppUser()) {
    return;
  }

  const loadConfig = hasToken ? loadSysConfigurations().catch(() => {}) : Promise.resolve();

  const loadBus = initBusConfigs().catch(() => {});

  const loadUser = hasToken
    ? getUserInfo()
        .then(() => redirectH5ToOpenProgressIfNeeded())
        .catch(() => {
          // token 无效时由拦截器处理；避免 UnhandledRejection
        })
    : Promise.resolve();

  await Promise.all([loadConfig, loadBus, loadUser]);
};

export const App = () => {
  const state = reactive({
    loading: true,
    pcShell: isPcShellViewport(),
  });

  onMount(() => {
    const syncShell = () => {
      state.pcShell = isPcShellViewport();
    };
    syncShell();
    window.addEventListener('resize', syncShell);
    onCleanup(() => window.removeEventListener('resize', syncShell));
  });

  // 最早判定 APP / H5 / 渠道 H5，避免与 APP 登录态混用
  initUserClientType();
  initAnalytics();

  // 与 ?lang= 同通道：URL / localStorage 保首屏主题，避免暗色 App 内白闪
  initTheme();
  initLineTheme();
  console.log(`H5开始准渲染： 1 查看ReactNativeWebView============》(window as any).ReactNativeWebView =${(window as any).ReactNativeWebView}`, );
  const isOpenAccountAppWebView = Boolean((window as any).ReactNativeWebView) && isOpenAccountDocument();
  if ((window as any).ReactNativeWebView && !isOpenAccountAppWebView) {
    // 最早初始化 RNBridge 分发器，确保后续所有模块的 registerRNHandler 能立即生效
    initRNBridge();

    // 通知app端，web端已就绪
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'WEB_TO_APP_WebReady',
        payload: 'Web bridge initialized',
      })
    );

    window.addEventListener('APP_TO_WEB_UserToken', (e) => {
      const customEvent = e as CustomEvent<any>;
      const data = customEvent.detail?.payload;
      const token = data?.token as string | undefined;
      // 相同 token 去重：避免 App 重复下发导致多次副作用
      // 脱敏日志在 rnBridge.persistTokenFromMessage（dispatch 入口）统一打
      const changed = applyRNUserToken(token);
      if (!changed) return;
      userState.isLogin = true;
      // configurations / userInfo / country / busConfigs 由 APP_TO_WEB_Theme 灌入，此处不再请求
    });

    // URL 保首屏；bridge：scheme / riseFallMode + configurations / country / busConfigs / userInfo / apply seed
    // 有值则灌入；无值 / {}（如 WebView 刷新未再带参）则兜底请求对应接口
    window.addEventListener('APP_TO_WEB_Theme', (e) => {
      console.log('APP_TO_WEB_Theme', e);
      const customEvent = e as CustomEvent<any>;
      const data = customEvent.detail?.payload ?? {};
      applyTheme(data?.scheme);
      if (data?.riseFallMode) {
        initLineTheme(data.riseFallMode);
      }

      const configurations = normalizeThemeDict(data.configurations);
      console.log('[encryptKey][APP_TO_WEB_Theme]', {
        hasConfigurations: !!configurations,
        encryptKey: configurations?.encryptKey != null && configurations.encryptKey !== ''
          ? String(configurations.encryptKey)
          : '(none)',
      });
      if (configurations) {
        applySysConfigurations(configurations);
      } else {
        console.log('[encryptKey][APP_TO_WEB_Theme] fallback → loadSysConfigurations');
        afterRNToken(() => loadSysConfigurations());
      }

      const country = normalizeThemeDict(data.country);
      if (country) {
        applyCountryFromApp(country);
      } else {
        afterRNToken(() => initCountry().catch(() => {}));
      }

      const busConfigsPayload = normalizeThemeDict(data.busConfigs);
      if (busConfigsPayload) {
        applyBusConfigsFromApp(busConfigsPayload);
      } else {
        afterRNToken(() => initBusConfigsFromApi().catch(() => {}));
      }

      const userInfo = normalizeThemeDict(data.userInfo);
      if (userInfo) {
        applyUserInfo(userInfo);
      } else {
        afterRNToken(() => getUserInfo().catch(() => {}));
      }

      // 申请 seed：applySeeded + applyType + applyInfo / applyList（无则业务页自行请求）
      applyApplySeedFromTheme(data);
    });

    // 与 URL ?lang= 双通道：URL 保首屏，bridge 保与 App 设置一致（payload.Lang / lang）
    window.addEventListener('APP_TO_WEB_Lang', (e) => {
      const customEvent = e as CustomEvent<any>;
      const payload = customEvent.detail?.payload;
      const raw = payload?.Lang ?? payload?.lang;
      void applyH5Lang(raw);
    });

    window.addEventListener('APP_TO_WEB_DeviceInfo', (e: Event) => {
      const customEvent = e as CustomEvent<any>;
      const deviceInfo = customEvent.detail.payload;
      //todo {"appName": "DiFiRN", "appVersion": "0.0.1", "deviceId": "7e05881f76c15f29", "deviceName": "Xiaomi 13", "isBiometric": true, "osName": "Android", "osVersion": "14"}
      userState.deviceInfo = deviceInfo;
    });

    window.addEventListener('APP_TO_WEB_DeviceCode', (e: Event) => {
      const customEvent = e as CustomEvent<any>;
      const { deviceCode } = customEvent.detail.payload;
      if (deviceCode) {
        localStorage.setItem('deviceCode', deviceCode);
      }
    });

    // App 下发安全区；未下发时 --footer-height 仍走 env(safe-area-inset-bottom) 兜底
    window.addEventListener('APP_TO_WEB_SafeArea', (e: Event) => {
      const customEvent = e as CustomEvent<any>;
      applySafeArea(customEvent.detail?.payload);
    });

    window.addEventListener('APP_TO_WEB_VerifyPwd', (e: Event) => {
      const customEvent = e as CustomEvent<any>;
      const data = customEvent.detail.payload;
      const password = data.password; // 需要验证的登录密码
      // 验证登录密码

      // 验证后需要调用 ReactNativeWebView.postMessage 方法，将验证后的结果返回给 APP 端
      (window as any).ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: 'WEB_TO_APP_WebReady',
          payload: 'Web bridge initialized',
        })
      );
    });

  }

  if ((window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'WEB_TO_APP_SetBarColor',
        payload: { color: '' }
      })
    )
  }

  if (!isOpenAccountAppWebView && localStorage.getItem('token')) {
    userState.isLogin = true;
  }

  // 开户 App WebView：等 OpenAccount Bootstrap；其他 WebView / 纯 H5：沿用旧 token 门闩
  void bootstrapAppData();

  // document.body.classList.add('dark');

  // initCountry();

  return (
    <div id="app" class="color font-s">
      {/* PC：430×932 壳。APP：按屏幕铺开，宽最多 1400。index.html 强制 le-480 保证移动端样式 */}
      <div
        id="h5-shell"
        class={`h5-shell${isAppShellPreview() ? ' h5-shell--app' : ''}${state.pcShell ? ' h5-shell--pc' : ''}`.trim()}
      >
        <KeepAlive show={true}>
          <MobileHost />
        </KeepAlive>
      </div>
    </div>
  );
};
